import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/stores/auth';
import { api } from '@/api/client';
import { progressApi } from '@/api/progress';
import { humanizeError } from '@/lib/errors';
import { chunkIds } from '@/lib/studentPoints';
import { enrollmentLine, filterMyStudents } from '@/lib/curatorList';
import { useIsMobile } from '@/hooks/useIsMobile';
import { MBlock, MEmpty, MRow, MTag, MTitle } from '@/components/mobile/MobileKit';
import { SearchInput } from '@/components/ui/SearchInput';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Avatar } from '@/components/ui/Avatar';
import { Pill } from '@/components/ui/Pill';

// GET /curator/students — массив строк ученика (CuratorStudentView users-service): имя, почта, аватар и активность
// из auth.users (прежняя пометка «имён не присылает» устарела), с 28.09.2026 — ещё курс и группа его записей у
// этого куратора (`enrollments`), по ним ищет поиск. Имени нет — строка рисуется заглушкой.
//
// ⚠️ `points` И `current_level` УБРАНЫ ИЗ ТИПА 12.09.2026 (договор
// `student-points.md` §5): users-service их больше не присылает. Баллы берутся у
// progress-service пакетной ручкой, уровень не показывается вовсе.
type CuratorStudent = {
  id: string;
  user_id: string;
  full_name?: string;
  email?: string;
  avatar_url?: string;
  last_activity_at?: string;
  enrollments?: { marathon_title: string; group_name: string; status: string }[];
};

export function CuratorStudentsPage() {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);
  const isMobile = useIsMobile();

  const students = useQuery({
    queryKey: ['curator', 'students-list'],
    queryFn: async () => {
      const { data } = await api.get('/curator/students');
      // Response is a bare array (client.ts does not alias bare arrays to
      // `.items`), so normalise it here.
      return (Array.isArray(data) ? data : ((data as { items?: CuratorStudent[] })?.items ?? [])) as CuratorStudent[];
    },
    enabled: !!me,
  });

  const all = students.data ?? [];
  const [q, setQ] = useState('');
  const items = filterMyStudents(all, q);
  // Баллы — на весь список, а не на найденных: поиск не должен заново дёргать пакетную ручку.
  const ids = all.map((u) => u.user_id ?? u.id).filter(Boolean);

  /**
   * Баллы всего списка — пакетной ручкой, а не запросом на каждого ученика.
   *
   * ⚠️ НАРЕЗКА ПО 500 ОБЯЗАТЕЛЬНА. Пагинации на этом экране нет: приходят все
   * ученики сразу, а у куратора их бывает 850. Один запрос на 850 id — 400 на
   * весь список, и экран остался бы вовсе без чисел (договор §3).
   *
   * Отказ пакетной ручки список не роняет: плашек с баллами просто не будет —
   * ложного нуля там больше нет, поля из users не приходят.
   */
  const pointsById = useQuery({
    queryKey: ['progress', 'students-points', ids],
    queryFn: async () => {
      const parts = await Promise.all(chunkIds(ids).map((chunk) => progressApi.getStudentsPoints(chunk)));
      return Object.assign({}, ...parts) as Record<string, number>;
    },
    enabled: ids.length > 0,
  });

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §4.3) ──────────────────────────
  // Строка 64 px вместо карточки: на экран 8–9 учеников, а не 3. Баллы — меткой справа, «был» — мелко под ней.
  if (isMobile) {
    return (
      <div className="space-y-3">
        <MTitle
          title={t('curatorStudents.title')}
          subtitle={students.isSuccess && all.length ? t('common.shownOf', { shown: items.length, total: all.length }) : undefined}
        />
        {students.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : students.isError ? (
          <p className="px-0.5 text-[13px] text-error">{humanizeError(students.error)}</p>
        ) : !all.length ? (
          <MEmpty icon="group" title={t('curatorStudents.noStudentsTitle')} hint={t('curatorStudents.noStudentsDesc')} />
        ) : (
          <>
            <SearchInput value={q} placeholder={t('curatorStudents.searchPlaceholder')} onSearch={setQ} />
            {!items.length ? (
              <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.nothingFound')}</p>
            ) : (
              <MBlock>
                {items.map((u, i) => {
                  const studentId = u.user_id ?? u.id;
                  const name = u.full_name || t('curatorStudents.studentFallback');
                  const { line, more } = enrollmentLine(u.enrollments);
                  const meta = line
                    ? more > 0 ? `${line} +${more}` : line
                    : u.email || (!u.full_name ? t('curatorStudents.studentCode', { code: studentId.slice(0, 8) }) : '');
                  const pts = pointsById.data?.[studentId];
                  return (
                    <MRow
                      key={u.id ?? studentId}
                      leading={<Avatar name={name} src={u.avatar_url} size="md" />}
                      title={name}
                      meta={meta || undefined}
                      to={`/curator/students/${studentId}`}
                      last={i === items.length - 1}
                      trailing={
                        <span className="flex flex-none flex-col items-end gap-1">
                          {typeof pts === 'number' && <MTag>{t('curatorStudents.points', { count: pts })}</MTag>}
                          {u.last_activity_at && (
                            <span className="text-[10.5px] text-on-surface-variant">
                              {t('curatorStudents.lastActivity', {
                                date: new Date(u.last_activity_at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }),
                              })}
                            </span>
                          )}
                        </span>
                      }
                    />
                  );
                })}
              </MBlock>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={t('curatorStudents.title')}
        subtitle={students.isLoading ? t('common.loading') : t('curatorStudents.studentCount', { count: all.length })}
      />
      {students.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : students.isError ? (
        <Card variant="subtle" className="text-center text-error">{humanizeError(students.error)}</Card>
      ) : !all.length ? (
        <Card variant="subtle" className="space-y-1 py-8 text-center text-on-surface-variant">
          <p className="font-semibold text-on-surface">{t('curatorStudents.noStudentsTitle')}</p>
          <p className="text-sm">{t('curatorStudents.noStudentsDesc')}</p>
        </Card>
      ) : (
        <>
        <SearchInput className="mb-2" value={q} placeholder={t('curatorStudents.searchPlaceholder')} onSearch={setQ} />
        <p className="mb-3 text-xs text-on-surface-variant">{t('common.shownOf', { shown: items.length, total: all.length })}</p>
        {!items.length && <Card variant="subtle" className="text-on-surface-variant">{t('common.nothingFound')}</Card>}
        <ul className="grid gap-3 md:grid-cols-2">
          {items.map((u) => {
            const studentId = u.user_id ?? u.id;
            // Backend /curator/students returns student_profiles rows only — name/email
            // are not joined from auth.users (see backendGaps), so full_name is usually
            // absent. Fall back to a clean placeholder rather than exposing a raw UUID.
            const name = u.full_name || t('curatorStudents.studentFallback');
            return (
              <li key={u.id ?? studentId}>
                <Link to={`/curator/students/${studentId}`}>
                  <Card interactive className="flex items-center gap-3">
                    <Avatar name={name} src={u.avatar_url} size="lg" />
                    <div className="flex-1">
                      <p className="font-semibold text-on-surface">{name}</p>
                      {u.email ? (
                        <p className="text-xs text-on-surface-variant">{u.email}</p>
                      ) : !u.full_name ? (
                        <p className="text-xs text-on-surface-variant">{t('curatorStudents.studentCode', { code: studentId.slice(0, 8) })}</p>
                      ) : null}
                      <div className="mt-1 flex flex-wrap gap-2">
                        {typeof pointsById.data?.[studentId] === 'number' && (
                          <Pill variant="success">
                            {t('curatorStudents.points', { count: pointsById.data[studentId] })}
                          </Pill>
                        )}
                        {u.last_activity_at && (
                          <Pill variant="neutral">
                            {t('curatorStudents.lastActivity', { date: new Date(u.last_activity_at).toLocaleDateString('ru-RU') })}
                          </Pill>
                        )}
                      </div>
                      {(u.enrollments ?? []).map((e, i) => (
                        <p key={i} className="mt-1 truncate text-xs text-on-surface-variant">
                          {e.group_name ? `${e.marathon_title} · ${e.group_name}` : e.marathon_title}
                        </p>
                      ))}
                    </div>
                  </Card>
                </Link>
              </li>
            );
          })}
        </ul>
        </>
      )}
    </>
  );
}
